/**
 * consolidateByCompany: 同一出展企業（companyRootId）に属する複数CVを1件に統合する（2026-08-03追加）。
 *
 * 背景: US-D2-10「複数CV併記表示」の想定は「1画面に企業ごとに1つの商談機会を提示する」ことだが、
 * spica-apiのresolveScenarioはCV（objectRootId）単位で判定結果を返すため、同一企業が複数のCV
 * （例: 同一企業のカタログを3件まとめてダウンロードした場合）を持つと、そのまま複数アイテムとして
 * 描画され重複表示になっていた（実機検証で検出）。
 *
 * 採用ルール（ユーザー確定要件、2026-08-03）:
 * 1. 統合の単位は「企業ごと」（companyRootId）。scenarioId単位ではない
 *    （同一企業が複数シナリオ・複数コンテンツグループを持つケースがあり得るため）。
 * 2. メインCVの中で同一企業の複数CVがある場合、アンケート画面（1画面前）のクエリパラメータの
 *    順番で最も左（data-cvs配列内で最も早く出現する）ものを採用する。
 * 3. メインCVにもレコメンドCVにも同一企業のものがある場合、メインCV側を優先する。
 * 4. レコメンドCVの中で同一企業のものが複数ある場合、採用条件は指定なし。本実装では
 *    objectRootIdの数値昇順で最小のものを採用する（決定的な挙動にするための実装上の選択）。
 * 5. companyRootIdが解決できない（null/undefined）CVは統合対象外とし、そのまま個別に残す
 *    （企業単位のグルーピングができないため、安全側に倒して非表示にはしない）。
 */

import type { ContentDisplayResult, SpicaWidgetCv } from "../types";

/**
 * visibleResultsを企業単位（companyRootId）で統合する。
 *
 * @param visibleResults fetchScenarioの結果からHIDDENを除いたリスト
 * @param cvs data-cvs属性由来の元の配列（クエリパラメータ順を保持している）。
 *            visibleResultsの各要素のobjectRootId+objectTypeでcvs内の出現順（index）を引く。
 * @returns 企業ごとに1件（companyRootIdが無いものは個別のまま）に絞り込んだ結果
 */
export function consolidateByCompany(
  visibleResults: ContentDisplayResult[],
  cvs: SpicaWidgetCv[]
): ContentDisplayResult[] {
  const orderIndex = new Map<string, number>();
  cvs.forEach((cv, index) => {
    orderIndex.set(cvKey(cv.objectType, cv.objectRootId), index);
  });

  const withCompany: ContentDisplayResult[] = [];
  const withoutCompany: ContentDisplayResult[] = [];
  for (const result of visibleResults) {
    if (result.companyRootId !== null && result.companyRootId !== undefined) {
      withCompany.push(result);
    } else {
      withoutCompany.push(result);
    }
  }

  const byCompany = new Map<number, ContentDisplayResult[]>();
  for (const result of withCompany) {
    const companyRootId = result.companyRootId as number;
    const list = byCompany.get(companyRootId);
    if (list) {
      list.push(result);
    } else {
      byCompany.set(companyRootId, [result]);
    }
  }

  const consolidated: ContentDisplayResult[] = [];
  for (const group of byCompany.values()) {
    consolidated.push(pickRepresentative(group, orderIndex));
  }

  return [...consolidated, ...withoutCompany];
}

/**
 * 同一企業のCV群から表示に採用する1件を選ぶ（ルール2〜4）。
 */
function pickRepresentative(
  group: ContentDisplayResult[],
  orderIndex: Map<string, number>
): ContentDisplayResult {
  const mainCvs = group.filter((r) => r.isMainCv);
  if (mainCvs.length > 0) {
    // ルール2: メインCVの中では、data-cvs配列内で最も早く出現するものを採用する
    return mainCvs.reduce((leftmost, current) => {
      const leftmostIndex = orderIndex.get(cvKey(leftmost.objectType, leftmost.objectRootId)) ?? Number.MAX_SAFE_INTEGER;
      const currentIndex = orderIndex.get(cvKey(current.objectType, current.objectRootId)) ?? Number.MAX_SAFE_INTEGER;
      return currentIndex < leftmostIndex ? current : leftmost;
    });
  }

  // ルール4: レコメンドCVのみの場合、objectRootIdの数値昇順で最小のものを採用する（決定的にするための実装上の選択）
  return group.reduce((smallest, current) => {
    const smallestId = Number(smallest.objectRootId);
    const currentId = Number(current.objectRootId);
    if (Number.isNaN(smallestId) || Number.isNaN(currentId)) {
      return smallest;
    }
    return currentId < smallestId ? current : smallest;
  });
}

function cvKey(objectType: string, objectRootId: string): string {
  return `${objectType}:${objectRootId}`;
}
